import { BandsintownShowDetails } from "@/components/integrations/BandsintownShowDetails";
import { useArtistScope } from "@/lib/artistContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

import { getCalendarIntegrations, getUnifiedCalendarEvents, runCalendarSync, type UnifiedEvent } from "@/api/calendarSync";
import { createManagerEvent, getManagerEvents, importManagerEvents, recordCashFlowInputs, updateManagerEvent } from "@/api/manager";
import PageHeader from "@/components/PageHeader";
import { ShowFinanceDrawer } from "@/components/manager/ShowFinanceDrawer";
import { OtherEventDrawer } from "@/components/manager/OtherEventDrawer";
import { CalendarSyncPanel } from "@/components/calendar/CalendarSyncPanel";
import { ShowImportDialog } from "@/components/manager/ShowImportDialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/components/ToastProvider";
import { useAgentResource } from "@/hooks/useAgentResource";
import type { ManagerEvent, OtherEventFormState, UpcomingGigFormState } from "@/types/manager";
import {
  combineDateAndTime,
  createUpcomingGigFormDefaults,
  currencyToNumber,
  eventToUpcomingGigForm,
  percentageToNumber,
} from "@/lib/showFinanceForm";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const toDayKey = (date: Date) => date.toDateString();

const parseCalendarDate = (value?: string | null): Date | null => {
  if (!value) return null;
  if (DATE_ONLY_PATTERN.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

type EventMetadata = {
  start_date?: string;
  end_date?: string;
};

const MAX_RANGE_DAYS = 90;

const buildDateRange = (start?: string | null, end?: string | null): Date[] => {
  const startDate = parseCalendarDate(start ?? undefined);
  if (!startDate) return [];
  const resolvedEnd = parseCalendarDate(end ?? undefined) ?? startDate;
  const endDate = resolvedEnd < startDate ? startDate : resolvedEnd;

  const range: Date[] = [];
  const cursor = new Date(startDate);
  let steps = 0;
  while (cursor <= endDate && steps < MAX_RANGE_DAYS) {
    range.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    steps += 1;
  }
  if (range.length === 0) {
    range.push(new Date(startDate));
  } else if (cursor <= endDate) {
    range[range.length - 1] = new Date(endDate);
  }
  return range;
};

const deriveEventDateRange = (event: ManagerEvent): Date[] => {
  const metadata = (event.metadata ?? {}) as EventMetadata;
  const startSource =
    typeof metadata.start_date === "string" && metadata.start_date.trim()
      ? metadata.start_date
      : event.event_date;
  const endSource =
    typeof metadata.end_date === "string" && metadata.end_date.trim()
      ? metadata.end_date
      : startSource;

  const fallbackStart = parseCalendarDate(event.event_date);
  return buildDateRange(startSource ?? fallbackStart?.toISOString(), endSource ?? startSource);
};

type CalendarFilter = "upcoming" | "past" | "all";

// Private beta: external calendar connections (Google OAuth / ICS) are not commissioned and
// are hidden. The implementation (CalendarSyncPanel, /calendar/*) is kept for post-beta work.
export const CALENDAR_CONNECT_ENABLED = false;

const createOtherEventDefaults = (): OtherEventFormState => ({
  event_name: "",
  description: "",
  start_date: "",
  end_date: "",
  notes: "",
});

const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const buildMonthWeeks = (month: Date) => {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start, end });
  const weeks: Date[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }
  return weeks;
};

type CalendarListItem =
  | { kind: "manager"; event: ManagerEvent }
  | { kind: "synced"; event: UnifiedEvent };

export default function CalendarPage() {
  const { selectedArtistId } = useArtistScope();
  return selectedArtistId ? <ArtistCalendarPage key={selectedArtistId} artistId={selectedArtistId} /> : <p>Select an artist to view shows.</p>;
}

function ArtistCalendarPage({ artistId }: { artistId: string }) {
  const { push } = useToast();
  const loadEvents = useCallback(async () => {
    const events: ManagerEvent[] = [];
    let offset: number | null = 0;
    while (offset !== null) {
      const page = await getManagerEvents(500, offset, artistId);
      events.push(...(page.events || []));
      const next = page.next_offset ?? null;
      if (next !== null && next <= offset) throw new Error("Invalid show pagination");
      offset = next;
    }
    return { events };
  }, [artistId]);
  const { data, loading, reload } = useAgentResource(loadEvents, {
    initialData: { events: [] },
    immediate: true,
  });
  const events = Array.isArray(data?.events) ? (data.events as ManagerEvent[]) : [];
  const [syncedEvents, setSyncedEvents] = useState<UnifiedEvent[]>([]);
  const [syncedLoading, setSyncedLoading] = useState(true);
  const [filter, setFilter] = useState<CalendarFilter>("upcoming");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ManagerEvent | null>(null);
  const [form, setForm] = useState<UpcomingGigFormState>(createUpcomingGigFormDefaults());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [monthCursor, setMonthCursor] = useState<Date>(() => new Date());
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement | null>(null);
  const [otherDrawerOpen, setOtherDrawerOpen] = useState(false);
  const [otherDrawerSaving, setOtherDrawerSaving] = useState(false);
  const [otherForm, setOtherForm] = useState<OtherEventFormState>(createOtherEventDefaults());
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importDirty, setImportDirty] = useState(false);
  // Set when Refresh skipped Bandsintown because no explicit artist link is configured.
  const [bandsintownNotice, setBandsintownNotice] = useState<string | null>(null);
  const manualMonthChange = useRef(false);

  const loadSyncedEvents = useCallback(async () => {
    setSyncedLoading(true);
    try {
      const data = await getUnifiedCalendarEvents(undefined, undefined, artistId);
      setSyncedEvents(data);
    } catch (error) {
      console.error("Failed to load unified events:", error);
    } finally {
      setSyncedLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    if (!addMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
        setAddMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [addMenuOpen]);

  useEffect(() => {
    loadSyncedEvents();
  }, [loadSyncedEvents]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const integrations = await getCalendarIntegrations(artistId);
      const connectedIntegrations = integrations.filter((integration) => integration.status === "connected");
      const syncPromises = connectedIntegrations.map((integration) => runCalendarSync(integration.provider, undefined, undefined, artistId));
      const importPromise = importManagerEvents({ date_filter: "upcoming", source: "all" }, artistId);

      const results = await Promise.allSettled([importPromise, ...syncPromises]);
      const importResult = results[0];
      if (importResult.status === "fulfilled") {
        const bandsintown = importResult.value?.bandsintown;
        setBandsintownNotice(
          bandsintown?.skipped
            ? String(bandsintown.reason || bandsintown.reason_code?.replaceAll("_", " ") || "Connect Bandsintown in Artist Settings.")
            : null,
        );
      }
      const failures = results.filter((result) => result.status === "rejected") as PromiseRejectedResult[];
      if (failures.length > 0) {
        push({
          title: "Refresh incomplete",
          message: "Some shows couldn't be refreshed. Please try again.",
        });
      }
    } catch (error) {
      push({
        title: "Refresh failed",
        message: error instanceof Error ? error.message : "Unable to refresh shows.",
      });
    } finally {
      setRefreshing(false);
      void reload();
      void loadSyncedEvents();
    }
  }, [loadSyncedEvents, push, reload]);

  const combinedEvents = useMemo<CalendarListItem[]>(() => {
    const managerShowIds = new Set(
      events
        .map((event) => event.show_id || event.external_id)
        .filter((value): value is string => Boolean(value && value.trim())),
    );

    const filteredSynced = syncedEvents.filter((event) => {
      const matchedId = event.bandsintown?.event_id;
      if (!matchedId) return true;
      return !managerShowIds.has(matchedId);
    });

    return [
      ...events.map((event) => ({ kind: "manager" as const, event })),
      ...filteredSynced.map((event) => ({ kind: "synced" as const, event })),
    ];
  }, [events, syncedEvents]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    combinedEvents.forEach((item) => {
      const range =
        item.kind === "manager" ? deriveEventDateRange(item.event) : buildDateRange(item.event.starts_at, item.event.ends_at);
      const id = item.kind === "manager" ? `manager:${item.event.id}` : `synced:${item.event.id}`;
      range.forEach((date) => {
        const key = toDayKey(date);
        const bucket = map.get(key);
        if (bucket) {
          bucket.push(id);
        } else {
          map.set(key, [id]);
        }
      });
    });
    return map;
  }, [combinedEvents]);

  const eventDayKeys = useMemo(() => new Set(eventsByDay.keys()), [eventsByDay]);

  const filteredEvents = useMemo(() => {
    const now = Date.now();
    return combinedEvents
      .filter((item) => {
        const dateValue = item.kind === "manager" ? item.event.event_date : item.event.starts_at;
        if (!dateValue) return true;
        const timestamp = new Date(dateValue).getTime();
        if (!Number.isFinite(timestamp)) return true;
        if (filter === "upcoming") return timestamp >= now;
        if (filter === "past") return timestamp < now;
        return true;
      })
      .sort((a, b) => {
        const aValue = a.kind === "manager" ? a.event.event_date : a.event.starts_at;
        const bValue = b.kind === "manager" ? b.event.event_date : b.event.starts_at;
        const aTime = aValue ? new Date(aValue).getTime() : 0;
        const bTime = bValue ? new Date(bValue).getTime() : 0;
        if (filter === "past") {
          return bTime - aTime;
        }
        return aTime - bTime;
      });
  }, [combinedEvents, filter]);

  const visibleEvents = useMemo(() => {
    if (!selectedDate) return filteredEvents;
    const dayKey = toDayKey(selectedDate);
    const dayEvents = eventsByDay.get(dayKey);
    if (!dayEvents || dayEvents.length === 0) {
      return [];
    }
    const ids = new Set(dayEvents);
    return filteredEvents.filter((item) => {
      const id = item.kind === "manager" ? `manager:${item.event.id}` : `synced:${item.event.id}`;
      return ids.has(id);
    });
  }, [eventsByDay, filteredEvents, selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      setMonthCursor((prev) => (isSameMonth(prev, selectedDate) ? prev : selectedDate));
      return;
    }
    if (manualMonthChange.current) {
      return;
    }
    setMonthCursor((prev) => (isSameMonth(prev, new Date()) ? prev : new Date()));
  }, [filteredEvents, selectedDate]);

  const monthLabel = format(monthCursor, "LLLL yyyy");
  const calendarWeeks = useMemo(() => buildMonthWeeks(monthCursor), [monthCursor]);
  const selectedDateLabel = selectedDate ? format(selectedDate, "PPP") : null;

  const openDrawerForEvent = (event?: ManagerEvent | null, presetDate?: string) => {
    setEditingEvent(event ?? null);
    setForm((_prev) => {
      const formState = eventToUpcomingGigForm(event);
      if (!event && presetDate) {
        formState.event_date = presetDate;
      }
      return formState;
    });
    setDrawerOpen(true);
  };

  const handleValueChange = (field: keyof UpcomingGigFormState, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOtherValueChange = (field: keyof OtherEventFormState, value: string) => {
    setOtherForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOtherDrawerSubmit = async () => {
    const trimmedName = otherForm.event_name.trim();
    if (!trimmedName) {
      push({ title: "Name required", message: "Give this event a name." });
      return;
    }
    if (!otherForm.start_date) {
      push({ title: "Start date required", message: "Choose when the block begins." });
      return;
    }
    if (otherForm.end_date && new Date(otherForm.end_date).getTime() < new Date(otherForm.start_date).getTime()) {
      push({ title: "Invalid range", message: "End date must be after the start date." });
      return;
    }
    const isoStart = combineDateAndTime(otherForm.start_date, "00:00");
    if (!isoStart) {
      push({ title: "Invalid date", message: "Unable to parse the provided start date." });
      return;
    }
    const metadata: Record<string, string> = {
      category: "other",
      start_date: otherForm.start_date,
    };
    if (otherForm.end_date) {
      metadata.end_date = otherForm.end_date;
    }
    if (otherForm.description.trim()) {
      metadata.description = otherForm.description.trim();
    }

    const payload: Record<string, unknown> = {
      event_date: isoStart,
      source: "calendar-other",
      name: trimmedName,
      venue: trimmedName,
      metadata,
    };
    if (otherForm.notes.trim()) {
      payload.notes = otherForm.notes.trim();
    }

    setOtherDrawerSaving(true);
    try {
      await createManagerEvent(payload);
      push({ title: "Event saved", message: `${trimmedName} added.` });
      setOtherDrawerOpen(false);
      setOtherForm(createOtherEventDefaults());
      await reload();
    } catch (error) {
      push({
        title: "Save failed",
        message: error instanceof Error ? error.message : "Unable to save the event.",
      });
    } finally {
      setOtherDrawerSaving(false);
    }
  };

  const handleVenueSelect = (selection: { id: number; label: string } | null) => {
    setForm((prev) => ({
      ...prev,
      venue_contact_id: selection?.id ?? null,
      venue_contact_label: selection?.label ?? "",
    }));
  };

  const handleContactSelect = (selection: { id: number; label: string } | null) => {
    setForm((prev) => ({
      ...prev,
      contact_id: selection?.id ?? null,
      contact_label: selection?.label ?? "",
    }));
  };

  const handleDrawerSubmit = async () => {
    if (!form.venue.trim()) {
      push({ title: "Venue required", message: "Add a venue before saving." });
      return;
    }
    if (!form.event_date) {
      push({ title: "Date required", message: "Select an event date." });
      return;
    }
    const showIdentifier =
      form.show_id || editingEvent?.show_id || editingEvent?.external_id || editingEvent?.id || `manual-${Date.now()}`;
    const isoDate = combineDateAndTime(form.event_date, form.event_time);
    if (!isoDate) {
      push({ title: "Invalid date", message: "Unable to parse the provided date/time." });
      return;
    }
    const guarantee = currencyToNumber(form.guarantee);
    const artistPct = percentageToNumber(form.artist_percentage);
    const sound = currencyToNumber(form.sound);
    const house = currencyToNumber(form.house);
    const marketing = currencyToNumber(form.marketing);

    const payload: Record<string, unknown> = {
      event_date: isoDate,
      show_id: showIdentifier,
      venue: form.venue.trim(),
      source: editingEvent ? editingEvent.source ?? "calendar" : "calendar",
    };
    if (form.city.trim()) payload.city = form.city.trim();
    if (form.state.trim()) payload.state = form.state.trim();
    if (form.notes.trim()) payload.notes = form.notes.trim();
    if (guarantee !== null) payload.guarantee_amount = guarantee;
    if (form.split.trim()) payload.split_terms = form.split.trim();
    if (artistPct !== null) payload.artist_percentage = artistPct;
    if (sound !== null) payload.sound_expenses = sound;
    if (house !== null) payload.house_expenses = house;
    if (form.venue_contact_id) payload.crm_venue_id = form.venue_contact_id;
    if (form.venue_contact_label) payload.crm_venue_name = form.venue_contact_label;
    if (form.contact_id) payload.crm_contact_id = form.contact_id;
    if (form.contact_label) payload.crm_contact_name = form.contact_label;

    setDrawerSaving(true);
    try {
      let eventId = editingEvent?.id ?? null;
      if (editingEvent) {
        await updateManagerEvent(editingEvent.id, payload);
      } else {
        const created = await createManagerEvent({
          ...payload,
          name: form.venue.trim(),
        });
        if (created?.id) {
          eventId = created.id;
        }
      }

      const request: Record<string, string | number | null> = {
        reference: showIdentifier,
        source: editingEvent ? "calendar-edit" : "calendar-manual",
        show_id: showIdentifier,
      };
      if (eventId) request.event_id = eventId;
      if (guarantee !== null) request.guarantee_amount = guarantee;
      if (form.split.trim()) request.split_terms = form.split.trim();
      if (artistPct !== null) request.artist_percentage = artistPct;
      if (marketing !== null) request.marketing_budget = marketing;
      if (sound !== null) request.sound_expenses = sound;
      if (house !== null) request.house_expenses = house;
      await recordCashFlowInputs(request);

      push({ title: "Calendar saved", message: `${form.venue.trim()} updated.` });
      setDrawerOpen(false);
      setEditingEvent(null);
      setForm(createUpcomingGigFormDefaults());
      await reload();
    } catch (error) {
      push({
        title: "Save failed",
        message: error instanceof Error ? error.message : "Unable to save calendar event.",
      });
    } finally {
      setDrawerSaving(false);
    }
  };

  return (
    <div className="aa-page-shell space-y-4">
      <section className="aa-glass-card p-4">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="aa-page-title aa-gradient-text">Shows</h1>
              <p className="text-xs text-muted-foreground">Your live dates, holds and travel in one place. Edit a show once and it updates everywhere.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {CALENDAR_CONNECT_ENABLED && (
                <button
                  type="button"
                  onClick={() => setSyncDialogOpen(true)}
                  className="rounded-lg border border-border/60 bg-background/80 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground/80 transition hover:border-primary/60 hover:text-primary"
                >
                  Sync calendars
                </button>
              )}
              <div className="relative" ref={addMenuRef}>
                <button
                  type="button"
                  onClick={() => setAddMenuOpen((prev) => !prev)}
                  className="flex items-center gap-2 rounded-lg border border-primary/60 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary transition hover:border-primary"
                  aria-haspopup="menu"
                  aria-expanded={addMenuOpen}
                >
                  Add calendar event
                  <ChevronDown className={`h-4 w-4 transition ${addMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {addMenuOpen && (
                  <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-border bg-background/95 p-3 shadow-xl">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Event type</p>
                    <div className="mt-3 space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          const presetDate = selectedDate ? selectedDate.toISOString().slice(0, 10) : undefined;
                          openDrawerForEvent(undefined, presetDate);
                          setAddMenuOpen(false);
                        }}
                        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-left transition hover:border-primary/50"
                      >
                        <p className="text-sm font-semibold text-foreground">Show</p>
                        <p className="text-xs text-muted-foreground">A booked performance, with optional deal terms.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const presetDate = selectedDate ? selectedDate.toISOString().slice(0, 10) : "";
                          setOtherForm((prev) => ({
                            ...prev,
                            start_date: prev.start_date || presetDate,
                            end_date: prev.end_date || presetDate,
                          }));
                          setAddMenuOpen(false);
                          setOtherDrawerOpen(true);
                        }}
                        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-left transition hover:border-emerald-500/60"
                      >
                        <p className="text-sm font-semibold text-emerald-300">Other</p>
                        <p className="text-xs text-muted-foreground">General holds, promo pushes, travel, etc.</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="rounded-lg border border-border/60 px-3 py-2 text-xs font-semibold text-foreground/70 transition hover:border-primary/60 hover:text-primary"
              >
                Import Shows
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleRefresh();
                }}
                className="rounded-lg border border-border/60 px-3 py-2 text-xs font-semibold text-foreground/70 transition hover:border-primary/60 hover:text-primary disabled:opacity-50"
                disabled={loading || syncedLoading || refreshing}
              >
                {loading || syncedLoading || refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
          {bandsintownNotice && (
            <div
              role="status"
              aria-label="Bandsintown not connected"
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground"
            >
              <span>
                <strong>Bandsintown shows weren't refreshed.</strong> {bandsintownNotice}
              </span>
              <Link
                to="/settings/artist"
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:opacity-90"
              >
                Open Artist profile
              </Link>
            </div>
          )}
          <div className="grid gap-4 items-start lg:grid-cols-[22rem_minmax(0,1fr)] xl:grid-cols-[26rem_minmax(0,1fr)]">
            <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    manualMonthChange.current = true;
                    setMonthCursor((prev) => addMonths(prev, -1));
                  }}
                  className="rounded-full border border-border/60 px-2 py-1 text-xs text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                >
                  Prev
                </button>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground">{monthLabel}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Tap a day to filter</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    manualMonthChange.current = true;
                    setMonthCursor((prev) => addMonths(prev, 1));
                  }}
                  className="rounded-full border border-border/60 px-2 py-1 text-xs text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                >
                  Next
                </button>
              </div>
              <div className="mt-4 grid grid-cols-7 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                {weekdayLabels.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-7 gap-1 text-center">
                {calendarWeeks.map((week, weekIndex) =>
                  week.map((day, dayIndex) => {
                    const key = `${weekIndex}-${dayIndex}-${day.toISOString()}`;
                    const isCurrentMonth = isSameMonth(day, monthCursor);
                    const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                    const isToday = isSameDay(day, new Date());
                    const hasEvent = eventDayKeys.has(day.toDateString());

                    let dayClasses =
                      "relative flex h-10 w-10 items-center justify-center rounded-full text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/60";
                    if (isSelected) {
                      dayClasses += " bg-primary text-primary-foreground shadow-inner shadow-primary/40";
                    } else if (isCurrentMonth) {
                      dayClasses +=
                        " border border-transparent text-foreground hover:border-primary/60 hover:bg-primary/10 bg-transparent";
                    } else {
                      dayClasses += " text-muted-foreground/70/70 bg-transparent";
                    }
                    if (isToday && !isSelected) {
                      dayClasses += " border border-primary/40";
                    }

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setMonthCursor(day);
                          setSelectedDate(day);
                          manualMonthChange.current = false;
                        }}
                        className={dayClasses}
                      >
                        {format(day, "d")}
                        {hasEvent && (
                          <span
                            className={`pointer-events-none absolute bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full shadow-[0_0_6px_rgba(59,130,246,0.55)] ${
                              isSelected ? "bg-background/40" : "bg-primary"
                            }`}
                          />
                        )}
                      </button>
                    );
                  }),
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground/80">
                <p>
                  {selectedDateLabel ? `Showing ${selectedDateLabel}` : "Select a day to filter events."}
                </p>
                {selectedDate && (
                  <button
                    type="button"
                    onClick={() => setSelectedDate(undefined)}
                    className="rounded-full border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-foreground/70 transition hover:border-primary/60 hover:text-primary"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wide">
                {(["upcoming", "past", "all"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFilter(option)}
                    className={`rounded-full border px-3 py-1 transition ${
                      filter === option
                        ? "border-primary/60 bg-primary/20 text-primary"
                        : "border-border/60 text-muted-foreground/80 hover:text-foreground"
                    }`}
                  >
                    {option === "upcoming" ? "Upcoming" : option === "past" ? "Past" : "All"}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                {loading || syncedLoading ? (
                  <p className="text-sm text-muted-foreground">Loading calendar…</p>
                ) : filteredEvents.length === 0 && combinedEvents.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border bg-background/40 p-8 text-center">
                    <p className="text-sm font-medium text-foreground">No shows scheduled</p>
                    <p className="mt-1 text-xs text-muted-foreground">Add your first gig or event to start tracking your calendar.</p>
                    <button
                      type="button"
                      onClick={() => openDrawerForEvent(null)}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      + Add a show
                    </button>
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No events match the selected filter.</p>
                ) : visibleEvents.length === 0 && selectedDate ? (
                  <p className="text-sm text-muted-foreground">No events scheduled for {selectedDateLabel}.</p>
                ) : (
                  visibleEvents.map((item) => {
                    if (item.kind === "manager") {
                      const event = item.event;
                      const eventDate = event.event_date ? new Date(event.event_date).toLocaleString() : "Date TBD";
                      const summary = [event.city, event.state].filter(Boolean).join(", ");
                      return (
                        <div key={`manager-${event.id}`}>
                        <button
                          type="button"
                          onClick={() => openDrawerForEvent(event)}
                          className="w-full rounded-2xl border border-border bg-background/60 px-4 py-3 text-left transition hover:border-primary/60"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-foreground">
                                {event.venue ?? event.name ?? "Untitled show"}
                              </p>
                              <p className="text-xs text-muted-foreground/80">
                                {eventDate}
                                {summary ? ` • ${summary}` : ""}
                              </p>
                            </div>
                            <div className="text-right text-xs text-muted-foreground/80">
                              {event.guarantee_amount != null && (
                                <p>Guarantee · ${Number(event.guarantee_amount).toLocaleString()}</p>
                              )}
                              {event.split_terms && <p>{event.split_terms}</p>}
                            </div>
                          </div>
                        </button>
                        <BandsintownShowDetails metadata={event.metadata} />
                        {event.source === "bandsintown" && event.source_urls?.[0] && <a className="px-4 text-xs underline" href={event.source_urls[0]} target="_blank" rel="noopener noreferrer">View on Bandsintown</a>}
                        </div>
                      );
                    }

                    const event = item.event;
                    const eventDate = event.starts_at ? new Date(event.starts_at).toLocaleString() : "Date TBD";
                    const syncedSummary = event.location ?? "";
                    return (
                      <div
                        key={`synced-${event.id}`}
                        className="w-full rounded-2xl border border-border bg-background/60 px-4 py-3 text-left"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">
                                {event.title || "Untitled event"}
                              </p>
                              <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                                Synced · {event.calendar_provider}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground/80">
                              {eventDate}
                              {syncedSummary ? ` • ${syncedSummary}` : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={importOpen} onOpenChange={open => {
        // Don't lose selected files/review choices to a stray outside click or Esc.
        if (!open && importDirty && !window.confirm("Close without importing? Your selected files and review choices will be lost.")) return;
        setImportOpen(open);
        if (!open) setImportDirty(false);
      }}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          {importOpen && <ShowImportDialog artistId={artistId} onDirtyChange={setImportDirty} onImported={() => { void reload(); }} />}
        </DialogContent>
      </Dialog>

      {CALENDAR_CONNECT_ENABLED && (
        <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
          <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
            <CalendarSyncPanel />
          </DialogContent>
        </Dialog>
      )}

      <ShowFinanceDrawer
        open={drawerOpen}
        title={editingEvent ? "Edit calendar event" : "Add calendar event"}
        subtitle={editingEvent ? "Synced show" : "Manual entry"}
        values={form}
        onValueChange={handleValueChange}
        onVenueSelect={handleVenueSelect}
        onContactSelect={handleContactSelect}
        onSubmit={handleDrawerSubmit}
        onClose={() => {
          setDrawerOpen(false);
          setEditingEvent(null);
          setForm(createUpcomingGigFormDefaults());
        }}
        saving={drawerSaving}
      />
      <OtherEventDrawer
        open={otherDrawerOpen}
        values={otherForm}
        onChange={handleOtherValueChange}
        onSubmit={handleOtherDrawerSubmit}
        onClose={() => {
          setOtherDrawerOpen(false);
          setOtherForm(createOtherEventDefaults());
        }}
        saving={otherDrawerSaving}
      />
    </div>
  );
}
